/**
 * Portal file viewer (app-builder primitive) — shows an app `file`-column value
 * INLINE: a PDF rendered to `<canvas>` by pdf.js (works on desktop AND mobile —
 * an `<iframe>` PDF doesn't render inline on iOS Safari), an image in `<img>`,
 * anything else as a compact download card. Reuses the chat `ImagePreview` seam:
 * an authenticated fetch → `Blob` →
 * `URL.createObjectURL` (the attachment URL needs a Bearer header, so it can't
 * ride `<img src>`/`<iframe src>` directly), revoked on unmount / re-fetched
 * when the `ref` changes.
 *
 * The `file` value is the descriptor a file column stores:
 * `{ ref, filename, size?, content_type? }`. A missing/blank value renders the
 * empty state (a page just opened, nothing selected yet).
 */
import { Download, FileText } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { portalApi } from "@/lib/api-portal";

import PortalPdfViewer from "./PortalPdfViewer";

type FileDescriptor = {
  ref: string;
  filename?: string;
  size?: number;
  content_type?: string;
};

/** Narrow an arbitrary bound value (`{{ $page.selectedDoc.file }}`) to a file
 *  descriptor — anything without a string `ref` is "no file". */
function asDescriptor(value: unknown): FileDescriptor | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.ref !== "string" || !v.ref) return null;
  return {
    ref: v.ref,
    filename: typeof v.filename === "string" ? v.filename : undefined,
    size: typeof v.size === "number" ? v.size : undefined,
    content_type: typeof v.content_type === "string" ? v.content_type : undefined,
  };
}

function kindOf(d: FileDescriptor): "pdf" | "image" | "other" {
  const ct = (d.content_type ?? "").toLowerCase();
  const name = (d.filename ?? "").toLowerCase();
  if (ct.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|avif)$/.test(name)) return "image";
  return "other";
}

/** The MIME to stamp on the object URL. The download route may serve
 *  `application/octet-stream` (→ the browser DOWNLOADS instead of rendering, the
 *  "Unknown file" bug), so we re-type the blob from the descriptor / inferred
 *  kind before `createObjectURL`. */
function mimeFor(d: FileDescriptor): string {
  if (d.content_type) return d.content_type;
  const k = kindOf(d);
  if (k === "pdf") return "application/pdf";
  return "application/octet-stream";
}


export function PortalFileViewer({
  appSlug,
  file,
  height,
  emptyText,
  showDownload = true,
}: {
  appSlug: string;
  file: unknown;
  /** Viewer height in px (PDF/image frame). Default 520. */
  height?: number;
  emptyText?: string;
  /** Show the filename + "Scarica" toolbar above the preview. Off = a bare
   *  thumbnail (a gallery of reference photos doesn't want a download per card). */
  showDownload?: boolean;
}) {
  const descriptor = asDescriptor(file);
  const ref = descriptor?.ref ?? null;
  // Stable per-file primitives (the descriptor object is recreated each render).
  const kind = descriptor ? kindOf(descriptor) : "other";
  const mime = descriptor ? mimeFor(descriptor) : "application/octet-stream";
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  // Fetch the bytes once per `ref` → object URL; revoke on change/unmount.
  useEffect(() => {
    if (!ref) {
      setUrl(null);
      setError(false);
      return;
    }
    let obj: string | null = null;
    let alive = true;
    setError(false);
    portalApi
      .attachmentBlob(appSlug, ref)
      .then((blob) => {
        if (!alive) return;
        // Re-type the blob so the browser knows it's a PDF/image and renders it
        // (or opens it correctly) instead of downloading an "Unknown file".
        const typed = blob.type === mime ? blob : new Blob([blob], { type: mime });
        obj = URL.createObjectURL(typed);
        setUrl(obj);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [appSlug, ref, mime]);

  const h = Math.max(160, Math.min(1200, Number(height) || 520));

  if (!descriptor) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-[var(--portal-radius)] border border-dashed border-border bg-surface/50 p-8 text-center text-sm text-muted-foreground"
        style={{ minHeight: 200 }}
      >
        <FileText className="h-6 w-6 opacity-50" />
        <span>{emptyText || "Nessun documento selezionato."}</span>
      </div>
    );
  }

  const name = descriptor.filename || "documento";
  const download = () => portalApi.downloadAttachment(appSlug, descriptor.ref, name);

  const chrome = (body: ReactNode) =>
    showDownload ? (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
            <FileText className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{name}</span>
          </div>
          <button
            type="button"
            onClick={download}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-[var(--portal-radius)] border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition hover:bg-elevated"
          >
            <Download className="h-4 w-4" />
            Scarica
          </button>
        </div>
        {body}
      </div>
    ) : (
      // Bare mode: no toolbar — just the preview (a gallery thumbnail).
      <>{body}</>
    );

  if (error) {
    return chrome(
      <div
        className="flex items-center justify-center rounded-[var(--portal-radius)] border border-border bg-surface p-6 text-sm text-muted-foreground"
        style={{ height: h }}
      >
        Anteprima non disponibile — usa Scarica.
      </div>,
    );
  }

  if (!url) {
    return chrome(
      <div
        className="animate-pulse rounded-[var(--portal-radius)] border border-border bg-muted/40"
        style={{ height: h }}
      />,
    );
  }

  if (kind === "pdf") {
    // pdf.js renders the PDF to <canvas> — inline on desktop AND mobile (an
    // <iframe> PDF fires a silent download on iOS Safari). pdf.js loads lazily
    // (dynamic import inside PortalPdfViewer). If it fails (huge/broken file), it
    // falls back to a user-gesture "Apri il PDF" card (`window.open`).
    const fallback = (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-[var(--portal-radius)] border border-dashed border-border bg-surface/50 p-8 text-center"
        style={{ minHeight: 200 }}
      >
        <FileText className="h-7 w-7 opacity-50" />
        <p className="text-sm text-muted-foreground">Anteprima non disponibile.</p>
        <button
          type="button"
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          className="inline-flex items-center gap-1.5 rounded-[var(--portal-radius)] bg-[var(--portal-accent,hsl(var(--accent)))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
        >
          <FileText className="h-4 w-4" />
          Apri il PDF
        </button>
      </div>
    );
    return chrome(<PortalPdfViewer url={url} height={h} fallback={fallback} />);
  }

  if (kind === "image") {
    return chrome(
      <div
        className="flex items-center justify-center overflow-auto rounded-[var(--portal-radius)] border border-border bg-surface p-2"
        style={{ maxHeight: h }}
      >
        <img src={url} alt={name} className="max-h-full max-w-full object-contain" />
      </div>,
    );
  }

  // Non-previewable type: a clean download card.
  return chrome(
    <div className="flex items-center justify-center rounded-[var(--portal-radius)] border border-border bg-surface p-6 text-sm text-muted-foreground">
      Anteprima non disponibile per questo tipo di file.
    </div>,
  );
}
